import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../firebaseConfig';
import { ApiResponse } from '../../Types/Common.type';
import './DailyReports.css';
import DailyReportsTable from '../../components/DailyReportsTable/DailyReportsTable';
import DailyReportsFilters, { DailyReportsFilters as DailyReportsFiltersType } from '../../components/DailyReportsFilters/DailyReportsFilters';
import { DailyReport } from '../../Types/DailyReport';
import { toast, ToastContainer } from 'react-toastify';
import { getTodayDateString } from '../../utils/date.utils';

export type PaginatedDailyReportsResponse = {
  dailyReports: DailyReport[];
  totalCount: number;
  nextCursor: string | null;
};

export type GetDailyReportsRequest = {
  pageSize?: number;
  lastSortKey?: string | null;
  filters: DailyReportsFiltersType;
};

const INITIAL_PAGE_SIZE = 10;

const DailyReports: React.FC = () => {
  const [dailyReports, setDailyReports] = useState<DailyReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<DailyReportsFiltersType>(() => ({
    start_date: getTodayDateString(),
    end_date: getTodayDateString(),
  }));
  const [totalCount, setTotalCount] = useState<number>(0);
  const [lastSortKey, setLastSortKey] = useState<string | undefined>(undefined);
  const [pageSize, setPageSize] = useState<number>(INITIAL_PAGE_SIZE);
  const [pageIndex, setPageIndex] = useState<number>(0);

  const isInitialMount = useRef(true);

  const fetchDailyReports = useCallback(async (currentFilters: DailyReportsFiltersType, currentPageSize: number, currentPageIndex: number, currentLastSortKey?: string) => {
    setIsLoading(true);
    try {
      const functions = getFunctions(app);
      const getPaginatedDailyReports = httpsCallable<GetDailyReportsRequest, ApiResponse>(functions, 'getPaginatedDailyReports');
      const payload: GetDailyReportsRequest = {
        lastSortKey: currentPageIndex > 0 ? currentLastSortKey : undefined,
        filters: currentFilters,
        pageSize: currentPageSize,
      };
      const result = (await getPaginatedDailyReports(payload)).data;
      if (result.code === 200) {
        const fetchedReports = result.data?.dailyReports || [];
        const totalRowCount = result.data?.totalCount || 0;
        setTotalCount(totalRowCount);
        setDailyReports(fetchedReports);
        const sortKey = fetchedReports.length > 0 ? fetchedReports[fetchedReports.length - 1].sort_key : undefined;
        setLastSortKey(sortKey);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error al recuperar reportes diarios', error);
      toast.error('Error al recuperar reportes diarios');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Single useEffect to handle all data fetching
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchDailyReports(filters, pageSize, pageIndex, lastSortKey);
    } else {
      // For subsequent calls, use current state values
      fetchDailyReports(filters, pageSize, pageIndex, lastSortKey);
    }
  }, [filters, pageSize, pageIndex, fetchDailyReports, lastSortKey]);

  const handleFilterSearch = useCallback((newFilters: DailyReportsFiltersType) => {
    setFilters(newFilters);
    setPageIndex(0);
    setLastSortKey(undefined);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPageIndex(0);
    setLastSortKey(undefined);
  }, []);

  const handlePageIndexChange = useCallback((newIndex: number) => {
    setPageIndex(newIndex);
  }, []);

  return (
    <div className="daily-reports-container">
      <div className="daily-reports-header">
        <h1 className="flex">Reportes Diarios</h1>
        <p className="flex">Gestión de reportes diarios de transacciones</p>
      </div>
      
      {/* Filters */}
      <div className="filters">
        <DailyReportsFilters
          onSearch={handleFilterSearch}
          isLoading={isLoading}
        />
      </div>
      
      <div className="daily-reports-content">
        <DailyReportsTable
          isLoading={isLoading}
          initialPageSize={pageSize}
          initialPageIndex={pageIndex}
          onPageSizeChange={handlePageSizeChange}
          onPageIndexChange={handlePageIndexChange}
          dailyReports={dailyReports}
          totalRowCount={totalCount}
        />
      </div>
      
      <ToastContainer />
    </div>
  );
};

export default DailyReports; 